import { defineStore } from "pinia";
import { ref } from "vue";
import {
    eMandateInstruction,
    // Aliased to avoid a name collision with the ref declared below
    eMandatesInformation as EMandatesInformation,
    ValidatedAccountAlias,
} from "@/helpers/interfaces/eMandates";

/**
 * eMandates store to manage eMandates-related data such as instructions,
 * information, account alias, and validated account information.
 * The store persists data in sessionStorage to maintain state across page
 * reloads within the same session.
 */
export const useEMandatesStore = defineStore(
    "emandates",
    () => {
        // ── State ──────────────────────────────────────────────────────────────
        const eMandatesInformation = ref<EMandatesInformation | null>(null);
        const eMandatesInstruction = ref<eMandateInstruction | null>(null);
        const accountAlias = ref<string | null>(null);
        const validatedAccountInformation = ref<ValidatedAccountAlias | null>(
            null,
        );

        // ── Actions ────────────────────────────────────────────────────────────
        function setEMandatesInformation(data: any) {
            eMandatesInformation.value = data;
        }

        function setEMandatesInstruction(
            instruction: eMandateInstruction | null,
        ) {
            eMandatesInstruction.value = instruction;
        }

        function setAccountAlias(alias: string | null) {
            accountAlias.value = alias;
        }

        function setValidatedAccountInformation(
            information: ValidatedAccountAlias | null,
        ) {
            validatedAccountInformation.value = information;
        }

        return {
            eMandatesInformation,
            eMandatesInstruction,
            accountAlias,
            validatedAccountInformation,
            setEMandatesInformation,
            setEMandatesInstruction,
            setAccountAlias,
            setValidatedAccountInformation,
        };
    },
    {
        persist: {
            pick: [
                "eMandatesInformation",
                "eMandatesInstruction",
                "accountAlias",
                "validatedAccountInformation",
            ],
            storage: sessionStorage,
        },
    },
);
